import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Autocomplete, CircularProgress, Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { customSxOutletForm } from "../../viewOrder/OutletFormUtils";
import { autocompleteLableMarginFix } from "../../../components/createSecondaryOrder/util";
import { clearBatchDetailsInDbPrimary, clearBatchDetailsInDbPrimaryReturn } from "../../SalesReturn/SalesReturnService";
import { useNavigate } from "react-router-dom";
import { getReturnableInvoices } from "../../../services/manageOrderService";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../../viewOrder/ViewOrderUtils";
import { getConfigData } from "../../../services/configurationService";
import { InfoIcon } from "../../../components/dms_common/InfoIcon";

type Option = any;

export interface CreateNewPrimaryReturnProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  closeOnSubmit?: boolean;
  canEditForm?: boolean;
  onSubmit?: (formData: Record<string, any>) => void;
}

export const CreateNewPrimaryReturn: React.FC<CreateNewPrimaryReturnProps> = ({
  open,
  onClose,
  title = "Create New Primary Return",
  closeOnSubmit = true,
  canEditForm = true,
  onSubmit,
}) => {
  const initialFormDataVal = {
    invoiceNumber: "",
    grnNumber: "",
    company: "",
  };

  const [initialFormData, setInitialFormData] = useState<Record<string, any>>(initialFormDataVal);
  const [formData, setFormData] = useState<Record<string, any>>(initialFormData);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(
    initialGenericModalProps
  );
  const [invoiceNumbers, setInvoiceNumbers] = useState<string[]>()
  const [grnNumbers, setGrnNumbers] = useState<string[]>()
  const [companys, setCompanys] = useState<string[]>()
  const [activeForm, setActiveForm] = useState<'Without GRN' | 'With GRN'>('With GRN');
  const [fields, setFields] = useState<string[]>(["company", "invoiceNumber", "grnNumber"]);

  const [invoiceGrnMapping, setInvoiceGrnMapping] = useState<Record<string, string>>({})

  const [returnableInvoices, setReturnableInvoices] = useState<any[]>([])

  const [allowedReturnQtyTypes, setAllowedReturnQtyTypes] = useState<string[]>(["ACCEPTED", "REJECTED"]);

  const getFilteredReturnableInvoices = (invoices: any[], allowedReturns: string[]) => {
    return invoices.filter((invoice) => {
      if (allowedReturns.includes("REJECTED") && invoice?.grn_status?.toLowerCase() === "rejected") {
        return true;
      }
      if (allowedReturns.includes("ACCEPTED") && invoice?.grn_status?.toLowerCase() === "accepted") {
        return true;
      }
      if (invoice.grn_status.toLowerCase() === "partially_rejected") {
        return true;
      }
      return false;
    });
  }


  useEffect(() => {
    const run = async () => {
      try {

      const res = await getReturnableInvoices();

      const allowedReturnsConfig = await getConfigData("dms_configuration", "allowedReturnQtyTypes");
      const allowedReturns = allowedReturnsConfig[0]?.configValue ?? [
        "ACCEPTED",
        "REJECTED",
      ];
      setAllowedReturnQtyTypes(allowedReturns);
      //Based on allowedReturntypes, filter grn rejected / grn accepted / partial returnable invoices

      const filteredInvoices = getFilteredReturnableInvoices(res, allowedReturns);
      setReturnableInvoices(filteredInvoices);

      const distinctCompanies = Array.from(
        new Set(res.map((item: any) => item.supplierid as string))
      ) as string[];

      if (distinctCompanies.length === 1) {
        setFormData((prev) => ({...prev, company: distinctCompanies[0]}))
      }

      setInitialFormData((prev) => ({...prev, company: distinctCompanies.length === 1 ? distinctCompanies[0] : ""}))

      setCompanys(distinctCompanies);
      } catch (error) {
        console.error("Error fetching returnable invoices:", error);
        setGenericModalProps(getGenericModalProps(
          "error",
          "Something went wrong"
        ))
      } finally {
      }
    }
    if (open) run();
  }, [open]);


  useEffect(() => {
    if(formData.company){
      const filteredInvoices = returnableInvoices.filter(item => item.supplierid === formData.company);
      const distinctInvoiceNumbers = Array.from(
        new Set(filteredInvoices.map((item: any) => item.invoice_number as string))
      ) as string[];

      setInvoiceNumbers(distinctInvoiceNumbers);
     
      const distinctGrnNumbers = Array.from(
        new Set(filteredInvoices.map((item: any) => item.grn_number as string))
      ) as string[];
      setGrnNumbers(distinctGrnNumbers);

      distinctInvoiceNumbers.forEach(invoiceNum => {
        const invoiceItem = filteredInvoices.find(item => item.invoice_number === invoiceNum);
        if(invoiceItem){
          setInvoiceGrnMapping((prev) => ({...prev, [invoiceNum]: invoiceItem.grn_number}))
          setInvoiceGrnMapping((prev) => ({...prev, [invoiceItem.grn_number]: invoiceNum}))
        }
      })
 
    }
    else {
      setInvoiceNumbers([]);
      setGrnNumbers([]);
    }
    setFormData((prev) => ({...prev, invoiceNumber: "", grnNumber: ""}))
  }, [formData.company, returnableInvoices]);


  useEffect(() => {
    if(formData.invoiceNumber){
      setFormData((prev) => ({...prev, grnNumber: invoiceGrnMapping[formData.invoiceNumber]}))
    }
    else {
      setFormData((prev) => ({...prev,  grnNumber: ""}))
    }
  }, [formData.invoiceNumber,returnableInvoices]);


  useEffect(() => {
    if(formData.grnNumber){
      setFormData((prev) => ({...prev, invoiceNumber: invoiceGrnMapping[formData.grnNumber]}))
    }
    else {
      setFormData((prev) => ({...prev,  invoiceNumber: ""}))
    }
  }, [formData.grnNumber,returnableInvoices]);


  const navigate = useNavigate();
  
  const styles = {
    formGroup: { 
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      justifyContent: "space-between", 
    } as React.CSSProperties,
    formLabel: { 
      fontSize: "0.88em",
      font: "Poppins', sans-serif !important",
      marginRight: "10px",
      width: "150px",
      fontWeight: 500,
      paddingLeft: "5px"
    } as React.CSSProperties,
     toggleContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    } as React.CSSProperties,
    toggleWrapper: {
      display: 'flex',
      gap: '8px',
      backgroundColor: '#f3f4f6',
      padding: '4px',
      borderRadius: '8px',
      width: '100%'
    },
    toggleButton: {
      flex: 1,
      padding: '10px 8px',
      borderRadius: '6px',
      fontWeight: '500',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontSize: '13px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: "5px"
    },
    toggleButtonActive: {
      backgroundColor: 'white',
      color: '#111827',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    toggleButtonInactive: {
      backgroundColor: 'transparent',
      color: '#6b7280'
    },
  };


  const handleAutocompleteSelect = (
    fieldKey: string,
    newValue: Option | string,
    optionsArray: Option[]
  ) => {
    if (newValue === null || newValue === undefined || newValue === "") {
      setFormData((prev) => ({ ...prev, [fieldKey]: "" }));
      return;
    }
    setFormData((prev) => ({ ...prev, [fieldKey]: newValue }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    await clearBatchDetailsInDbPrimary("primaryReturn");
    await clearBatchDetailsInDbPrimaryReturn('primary-return');



    if (activeForm === 'With GRN') {
      if (!formData.invoiceNumber || formData.invoiceNumber.trim() === "") {
        setGenericModalProps(
          getGenericModalProps("error", "Please select Invoice Number")
        );
        return;
      }
      navigate("create", {
        state: {
          invoiceNumber: formData.invoiceNumber,
          grnNumber: formData.grnNumber
        }
      })
    } else {
      if (!formData.company || formData.company.trim() === "") {
        setGenericModalProps(
          getGenericModalProps("error", "Please select Supply Chain")
        );
        return;
      }
      navigate("createWithoutGRN", {
        state: {
          company: formData.company,
        }
      })
    }
    if (closeOnSubmit) onClose();
  };

  useEffect(() => {
    if (!open) {
      setFormData({
        invoiceNumber: "",
        grnNumber: "",
        company: "",
      });
    }
  }, [open]);

  const fieldsForWithGrn = ["company", "invoiceNumber", "grnNumber"];

  const getLabel = (key: string) => {
    switch(key){
      case "company":
        return "Supply Chain"
      case "invoiceNumber":
        return "Invoice No."
      case "grnNumber":
        return "GRN No."
      default:
        return ""
    }
  }

  const getOptionList = (key: string) => {
    switch(key){
      case "company":
        return companys
      case "invoiceNumber":
        return invoiceNumbers
      case "grnNumber":
        return grnNumbers
      default:
        return []
    }
  }

  const fieldsForWithoutGrn = ["company"]

  useEffect(() => {
    if(activeForm === 'With GRN'){
      setFields(fieldsForWithGrn)
      setFormData(initialFormData)
    }
    else {
      setFields(fieldsForWithoutGrn)
      setFormData(initialFormData)
    }
  }, [activeForm])

  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const isButtonDisabled = () => {
    if(activeForm === 'With GRN'){
      return !formData.invoiceNumber;
    }
    else {
      return !formData.company;
    }
  }

  useEffect(() => {
    setIsDisabled(isButtonDisabled());
  }, [formData, activeForm]);


  return (
    <>
    <CustomGenericModal
      type={genericModalProps?.type}
      message={genericModalProps?.message}
      leftButtonText={genericModalProps?.leftButtonText}
      modalOpen={genericModalProps?.open}
      onClose={() => setGenericModalProps(initialGenericModalProps)}
    />
    <Dialog open={open} onClose={onClose} fullWidth
      sx={{
        "& .MuiPaper-root":{
          overflow: "visible",
          boxShadow: "none", borderRadius: "12px",
          maxWidth: "450px"
        }
      }}>


      <form style={{padding: "20px", minHeight: "420px", display: "flex", flexDirection: "column", justifyContent: "space-between"}}>
        <div style={{display: 'flex', flexDirection: "column", gap: "12px"}}>
          <div style={{display: "flex", justifyContent: "space-between"}}>

            <div style={{fontSize: "1.1rem", fontWeight: 600}}>
              Create Return
            </div>
            <div style={{cursor: "pointer", color: "#7c7c7c"}}
              onClick={onClose}
            >
              <CloseIcon />
            </div>
          </div>
          <div style={styles.toggleContainer}>
            <div style={styles.toggleWrapper} >
              <button
                type="button"
                onClick={() => setActiveForm('Without GRN')}
                style={{
                  ...styles.toggleButton,
                  ...(activeForm === 'Without GRN'
                    ? styles.toggleButtonActive
                    : styles.toggleButtonInactive),
                  }}
              >
                  <InfoIcon message="Raise returns for damaged, expired, or unsellable stock present in your inventory." 
                    bottom= {true}
                  />
                  Unsellable Stock Return
              </button>
              <button
                type="button"
                onClick={() => setActiveForm('With GRN')}
                style={{
                ...styles.toggleButton,
                ...(activeForm === 'With GRN'
                  ? styles.toggleButtonActive
                  : styles.toggleButtonInactive),
                }}
              >
                  <InfoIcon message={`Raise returns for stock ${allowedReturnQtyTypes.includes("ACCEPTED") ? "accepted" : ""} ${allowedReturnQtyTypes.includes("REJECTED") ? "rejected" : "" } or partially rejected during GRN.`} 
                    bottom= {true}
                  />

                  GRN Return
              </button>
            </div>
          </div>
          <Grid container sx={{gap: "8px"}}>
            {fields.map((field) => {
              const label = getLabel(field);
              const optionsList = getOptionList(field) ?? [];
              return (
                <Grid item xs={12} key={field} sx={{ mb: 1 }}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>{label}</label>

                    <FormControl
                      size="small"
                      className="custom_form_control"
                      onContextMenu={(e) => e.preventDefault()}
                      sx={{ ...customSxOutletForm, width: "100%",
                        "& .MuiInputBase-root":{
                          borderRadius: "12px"
                        }
                       }}
                    >
                      <Autocomplete
                        id={`${field}-autocomplete`}
                        openOnFocus
                        includeInputInList
                        clearOnBlur={false}
                        options={optionsList}
                        onChange={(event, newValue) =>
                          handleAutocompleteSelect(field, newValue ?? "", optionsList)
                        }
                        value={formData[field] ?? null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Here"
                            size="small"
                            fullWidth
                          />
                        )}
                        fullWidth
                        disableClearable={false}
                        ListboxProps={{ style: { maxHeight: 150 } }}
                        sx={{ ...customSxOutletForm, ...autocompleteLableMarginFix }}
                        disabled={!canEditForm}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{ color: "#464646", fontSize: "14px" }}
                          >
                            {option}
                          </li>
                        )}
                        noOptionsText="No options"
                      />
                    </FormControl>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        </div>

        <div style={{display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "10px"}}>
          <Button
            variant="outlined"
            sx={{
              color: "black",
              border: "0.5px solid #7c7c7c47",
              "&:hover": {
                color: "black",
                borderColor: "#7c7c7c47",
                backgroundColor: "#a5a5a542",
              },
              textTransform: "none",
              fontSize: "0.8em"
            }}
            onClick={onClose}
          >
            Cancel
        </Button>
          <Button type="submit" variant="contained"
            onClick={handleSubmit}
            disabled={isDisabled}
            sx={{
              backgroundColor: !isDisabled ? "#00a392" : "#bdbdbd",
              fontSize: "0.8em",
              boxShadow: "none",
              color: !isDisabled ? "white" : "#f5f5f5",
              cursor: !isDisabled ? "pointer" : "not-allowed",
              transition: "background-color 0.2s ease",
              "&:hover": {
                backgroundColor: !isDisabled ? "#008f7f" : "#bdbdbd",
                boxShadow: "none",
              },
              textTransform: "none"
            }}
          >
            Create Return
          </Button>
        </div>
      </form>
    </Dialog>
    </>
  );
};

export default CreateNewPrimaryReturn;
